// Smooth drag-chase for the dock firefly — samples tab anchor from the DOM
// and flies with capped velocity until the resting anchor is reached.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  blendChaseTarget,
  FIREFLY_CHASE_SETTLE_TARGET_BLEND,
  FIREFLY_DRAG_CHASE_SAMPLE_MS,
  stepFireflyChaseMotion,
} from "./fireflyChaseMotion";

export {
  anchorScreenFromDragCenter,
  blendChaseTarget,
  FIREFLY_CHASE_HEADING_BLEND,
  FIREFLY_CHASE_MAX_ACCEL_PX,
  FIREFLY_CHASE_MAX_SPEED_PX,
  FIREFLY_CHASE_SETTLE_SPEED_MULT,
  FIREFLY_CHASE_SETTLE_TARGET_BLEND,
  FIREFLY_DRAG_CHASE_SAMPLE_MS,
  fireflyAnchorScreenFromTabRect,
  lerpAngleRad,
  stepFireflyChaseMotion,
} from "./fireflyChaseMotion";

export function useFireflyDragChase(options: {
  isDragging: boolean;
  getTabAnchorScreen: () => { x: number; y: number } | null;
  onSettledAtAnchor?: () => void;
}) {
  const { isDragging, getTabAnchorScreen, onSettledAtAnchor } = options;

  const getTabAnchorScreenRef = useRef(getTabAnchorScreen);
  getTabAnchorScreenRef.current = getTabAnchorScreen;

  const [viewportX, setViewportX] = useState(0);
  const [viewportY, setViewportY] = useState(0);
  const [settlingAfterDrag, setSettlingAfterDrag] = useState(false);

  const currentRef = useRef({ x: 0, y: 0 });
  const velocityRef = useRef({ vx: 0, vy: 0 });
  const targetRef = useRef({ x: 0, y: 0 });
  const smoothedTargetRef = useRef({ x: 0, y: 0 });
  const wasDraggingRef = useRef(false);
  /** Sync flag — avoids one-frame active=false gap on release before state flushes. */
  const settlingRef = useRef(false);
  const rafRef = useRef<number | undefined>(undefined);
  const sampleTimerRef = useRef<number | undefined>(undefined);
  const onSettledRef = useRef(onSettledAtAnchor);
  onSettledRef.current = onSettledAtAnchor;

  const prevIsDraggingRef = useRef(isDragging);
  if (prevIsDraggingRef.current && !isDragging) {
    settlingRef.current = true;
  }
  if (isDragging) {
    settlingRef.current = false;
  }
  prevIsDraggingRef.current = isDragging;

  const active = isDragging || settlingRef.current || settlingAfterDrag;

  const readAnchorScreen = () => getTabAnchorScreenRef.current();

  const syncFromTab = (resetVelocity: boolean) => {
    const screen = readAnchorScreen();
    if (!screen) return false;
    currentRef.current = { ...screen };
    targetRef.current = { ...screen };
    smoothedTargetRef.current = { ...screen };
    setViewportX(screen.x);
    setViewportY(screen.y);
    if (resetVelocity) {
      velocityRef.current = { vx: 0, vy: 0 };
    }
    return true;
  };

  useEffect(() => {
    if (!active) {
      syncFromTab(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when chase ends
  }, [active]);

  useLayoutEffect(() => {
    if (isDragging) {
      if (!wasDraggingRef.current) {
        wasDraggingRef.current = true;
        const wasSettling = settlingRef.current;
        settlingRef.current = false;
        setSettlingAfterDrag(false);
        const screen = readAnchorScreen();
        if (screen) {
          targetRef.current = { ...screen };
          if (wasSettling) {
            smoothedTargetRef.current = { ...currentRef.current };
          } else {
            const dist = Math.hypot(
              currentRef.current.x - screen.x,
              currentRef.current.y - screen.y,
            );
            if (dist > 12) {
              smoothedTargetRef.current = { ...currentRef.current };
            } else {
              currentRef.current = { ...screen };
              smoothedTargetRef.current = { ...screen };
              setViewportX(screen.x);
              setViewportY(screen.y);
            }
          }
        }
        velocityRef.current = { vx: 0, vy: 0 };
      }
      return;
    }

    if (wasDraggingRef.current) {
      wasDraggingRef.current = false;
      settlingRef.current = true;
      setSettlingAfterDrag(true);
      const screen = readAnchorScreen();
      if (screen) {
        targetRef.current = { ...screen };
      }
    }
  }, [isDragging]);

  useEffect(() => {
    if (!isDragging) {
      if (sampleTimerRef.current !== undefined) {
        window.clearInterval(sampleTimerRef.current);
        sampleTimerRef.current = undefined;
      }
      return;
    }

    const sample = () => {
      const screen = readAnchorScreen();
      if (!screen) return;
      targetRef.current = screen;
    };

    sample();
    sampleTimerRef.current = window.setInterval(
      sample,
      FIREFLY_DRAG_CHASE_SAMPLE_MS,
    );

    return () => {
      if (sampleTimerRef.current !== undefined) {
        window.clearInterval(sampleTimerRef.current);
        sampleTimerRef.current = undefined;
      }
    };
  }, [isDragging]);

  useEffect(() => {
    if (!active) {
      if (rafRef.current !== undefined) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = undefined;
      }
      return;
    }

    let cancelled = false;

    const tick = () => {
      if (cancelled) return;

      const phase =
        (settlingRef.current || settlingAfterDrag) && !isDragging
          ? "settle"
          : "pursuit";

      if (phase === "settle") {
        const screen = readAnchorScreen();
        if (screen) {
          targetRef.current = screen;
          smoothedTargetRef.current = blendChaseTarget(
            smoothedTargetRef.current,
            screen,
            FIREFLY_CHASE_SETTLE_TARGET_BLEND,
          );
        }
      } else {
        smoothedTargetRef.current = blendChaseTarget(
          smoothedTargetRef.current,
          targetRef.current,
        );
      }

      const next = stepFireflyChaseMotion(
        { ...currentRef.current, ...velocityRef.current },
        smoothedTargetRef.current,
        phase,
      );
      currentRef.current = { x: next.x, y: next.y };
      velocityRef.current = { vx: next.vx, vy: next.vy };
      setViewportX(next.x);
      setViewportY(next.y);

      if (next.arrived && phase === "settle") {
        settlingRef.current = false;
        setSettlingAfterDrag(false);
        onSettledRef.current?.();
        return;
      }

      rafRef.current = window.requestAnimationFrame(tick);
    };

    rafRef.current = window.requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      if (rafRef.current !== undefined) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = undefined;
      }
    };
  }, [active, isDragging, settlingAfterDrag]);

  const forceSettleAtAnchor = useCallback(() => {
    if (sampleTimerRef.current !== undefined) {
      window.clearInterval(sampleTimerRef.current);
      sampleTimerRef.current = undefined;
    }
    if (rafRef.current !== undefined) {
      window.cancelAnimationFrame(rafRef.current);
      rafRef.current = undefined;
    }

    const screen = readAnchorScreen();
    if (screen) {
      currentRef.current = { ...screen };
      targetRef.current = { ...screen };
      smoothedTargetRef.current = { ...screen };
      setViewportX(screen.x);
      setViewportY(screen.y);
    }
    velocityRef.current = { vx: 0, vy: 0 };
    settlingRef.current = false;
    setSettlingAfterDrag(false);
    wasDraggingRef.current = false;
  }, []);

  return {
    active,
    viewportX,
    viewportY,
    settlingAfterDrag,
    forceSettleAtAnchor,
  };
}
